<template>
    <div class="max-w-(--breakpoint-md) flex flex-col gap-4">
        <page-header>Amplitude</page-header>

        <vx-expandable-card>
            <template v-slot:header>Inbound Configuration</template>
            <template v-slot:headerRight>
                <div v-if="loading">
                    <vx-badge color="slate">Loading</vx-badge>
                </div>
                <div v-else-if="inboundConnected">
                    <vx-badge color="success">Connected</vx-badge>
                </div>
                <div v-else>
                    <vx-badge color="slate">Not connected</vx-badge>
                </div>
            </template>

            <template v-slot:content>
                <div v-if="!props.ownsTeam" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                    Only the team owner can configure integrations.
                </div>
                <div v-else-if="inboundConnected" class="flex flex-col gap-6 mt-3">
                    <div>
                        To regenerate your credentials, click the button below. You will need to update them in your
                        <a
                            href="https://app.amplitude.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-blue-600 hover:underline"
                            data-test="amplitude-link"
                            >Amplitude Data Destinations</a
                        >
                        configuration.
                    </div>

                    <div class="flex gap-3">
                        <vx-button
                            color="primary"
                            size="lg"
                            @click="regenerateCredentials"
                            :loading="generating"
                            data-test="regenerate-button"
                        >
                            Regenerate Credentials
                        </vx-button>

                        <vx-button
                            color="danger"
                            size="lg"
                            @click="disconnectInbound"
                            :loading="disconnecting"
                            data-test="disconnect-button"
                        >
                            Disconnect
                        </vx-button>
                    </div>
                </div>
                <div v-else-if="!loading" class="flex flex-col gap-6 mt-3">
                    <div>
                        Click the button below to generate your Amplitude credentials. You will need to paste them into
                        your
                        <a
                            href="https://app.amplitude.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-blue-600 hover:underline"
                            data-test="amplitude-link"
                            >Amplitude Data Destinations</a
                        >
                        configuration.
                    </div>

                    <vx-button size="lg" @click="connect" :loading="generating" data-test="connect-button">
                        Connect
                    </vx-button>
                </div>
            </template>
        </vx-expandable-card>

        <vx-expandable-card data-test="outbound-config-card">
            <template v-slot:header>Outbound Configuration</template>
            <template v-slot:headerRight>
                <div v-if="loading">
                    <vx-badge color="slate">Loading</vx-badge>
                </div>
                <div v-else-if="outboundConnected">
                    <vx-badge color="success" data-test="api-key-badge">Connected</vx-badge>
                </div>
                <div v-else>
                    <vx-badge color="slate" data-test="api-key-badge">Not connected</vx-badge>
                </div>
            </template>
            <template v-slot:content>
                <div v-if="!props.ownsTeam" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                    Only the team owner can configure integrations.
                </div>
                <div v-else class="flex flex-col gap-6 mt-3">
                    <div>
                        Enter your Amplitude API key to enable outbound flows (sending contacts and events to
                        Amplitude). You can find your API key in Amplitude under
                        <strong>Organization settings → Connections → API Keys</strong>.
                    </div>
                    <div>
                        <vx-label size="md">Amplitude API Key</vx-label>
                        <vx-input
                            v-model="apiKeyInput"
                            size="lg"
                            placeholder="Enter your Amplitude API key"
                            :disabled="savingApiKey"
                            data-test="api-key-input"
                        />
                    </div>
                    <div v-if="!outboundConnected">
                        <vx-button
                            block
                            color="primary"
                            size="lg"
                            @click="saveApiKey"
                            :loading="savingApiKey"
                            data-test="save-api-key-button"
                        >
                            Connect
                        </vx-button>
                    </div>
                    <div v-else class="flex gap-3">
                        <vx-button
                            color="primary"
                            size="lg"
                            @click="saveApiKey"
                            :loading="savingApiKey"
                            data-test="save-api-key-button"
                        >
                            Update
                        </vx-button>
                        <vx-button
                            color="danger"
                            size="lg"
                            @click="disconnectOutbound"
                            :loading="disconnectingOutbound"
                            data-test="disconnect-outbound-button"
                        >
                            Disconnect
                        </vx-button>
                    </div>
                </div>
            </template>
        </vx-expandable-card>

        <amplitude-documentation />

        <!-- Credentials Modal -->
        <vx-modal
            v-model:visible="showCredentialsModal"
            preventClosing
            padding="standard"
            size="lg"
            data-test="credentials-modal"
        >
            <template v-slot:header>Amplitude Credentials</template>

            <vx-alert color="info" :closeable="false">
                Your credentials have been generated. Please copy them now and paste them into your Amplitude Data
                Destinations configuration. <strong>These values will only be shown once.</strong>
            </vx-alert>

            <div class="space-y-3 mt-4">
                <div>
                    <div class="text-sm font-medium mb-2">Key</div>
                    <vx-popover hover arrow placement="top">
                        <div
                            @click="copyValue(credentials?.key, 'Key')"
                            class="font-mono break-all cursor-pointer p-4 bg-white rounded-sm"
                            data-test="copy-key-button"
                        >
                            {{ credentials?.key }} <font-awesome-icon widthAuto :icon="faCopy" />
                        </div>
                        <template v-slot:content>Click to Copy</template>
                    </vx-popover>
                </div>

                <div>
                    <div class="text-sm font-medium mb-2">Secret</div>
                    <vx-popover hover arrow placement="top">
                        <div
                            @click="copyValue(credentials?.secret, 'Secret')"
                            class="font-mono break-all cursor-pointer p-4 bg-white rounded-sm"
                            data-test="copy-secret-button"
                        >
                            {{ credentials?.secret }} <font-awesome-icon widthAuto :icon="faCopy" />
                        </div>
                        <template v-slot:content>Click to Copy</template>
                    </vx-popover>
                </div>
            </div>

            <template v-slot:footer>
                <vx-button
                    block
                    color="primary"
                    size="lg"
                    @click="closeCredentialsModal"
                    data-test="close-modal-button"
                >
                    I've copied the credentials
                </vx-button>
            </template>
        </vx-modal>
    </div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue';
import {
    VxExpandableCard,
    VxButton,
    VxBadge,
    VxAlert,
    VxModal,
    VxPopover,
    VxSwal,
    VxInput,
    VxLabel,
} from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCopy } from '@fortawesome/pro-solid-svg-icons';
import PageHeader from '~/components/general/PageHeader.vue';
import AmplitudeDocumentation from './amplitude/AmplitudeDocumentation.vue';
import teamsClient from '~/services/teamsClient';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';

type Credentials = {
    key: string;
    secret: string;
};

const props = defineProps<{
    ownsTeam: boolean;
}>();

const toasted = useToasted();
const loading = ref(true);
const inboundConnected = ref(false);
const credentials = ref<Credentials | null>(null);
const showCredentialsModal = ref(false);
const generating = ref(false);
const disconnecting = ref(false);
const disconnectingOutbound = ref(false);
const outboundConnected = ref(false);
const apiKeyInput = ref('');
const savingApiKey = ref(false);

onMounted(async () => {
    try {
        const response = await teamsClient.getAmplitudeStatus(Spark.state.currentTeam.id);
        inboundConnected.value = response.data.inbound_connected ?? false;
        outboundConnected.value = response.data.outbound_connected ?? false;
    } catch (error) {
        console.error(error);
    } finally {
        loading.value = false;
    }
});

const connect = async () => {
    generating.value = true;
    try {
        const response = await teamsClient.connectAmplitudeInbound(Spark.state.currentTeam.id);
        credentials.value = response.data;
        inboundConnected.value = true;
        showCredentialsModal.value = true;
        toasted.success('Amplitude credentials generated successfully.');
    } catch (error) {
        toasted.error(catchRequestError(error));
    } finally {
        generating.value = false;
    }
};

const regenerateCredentials = () => {
    VxSwal.confirmation(
        'Regenerate Credentials',
        'Are you sure you want to regenerate your Amplitude credentials? You will need to update them in your Amplitude Data Destinations configuration, otherwise your integration will stop working.',
        'warning',
        async () => {
            generating.value = true;
            try {
                const response = await teamsClient.connectAmplitudeInbound(Spark.state.currentTeam.id);
                credentials.value = response.data;
                showCredentialsModal.value = true;
                toasted.success('Amplitude credentials regenerated successfully. Please update them in Amplitude.');
            } catch (error) {
                toasted.error(catchRequestError(error));
            } finally {
                generating.value = false;
            }
        }
    );
};

const disconnectInbound = () => {
    VxSwal.confirmation(
        'Disconnect Inbound',
        'Are you sure you want to disconnect your Amplitude inbound integration? Amplitude will no longer be able to send data to Voxie.',
        'danger',
        async () => {
            disconnecting.value = true;
            try {
                await teamsClient.disconnectAmplitudeInbound(Spark.state.currentTeam.id);
                inboundConnected.value = false;
                toasted.success('Amplitude inbound integration disconnected successfully.');
            } catch (error) {
                toasted.error(catchRequestError(error));
            } finally {
                disconnecting.value = false;
            }
        }
    );
};

const disconnectOutbound = () => {
    VxSwal.confirmation(
        'Disconnect Outbound',
        'Are you sure you want to disconnect your Amplitude outbound integration? Voxie will no longer send contacts and events to Amplitude.',
        'danger',
        async () => {
            disconnectingOutbound.value = true;
            try {
                await teamsClient.disconnectAmplitudeOutbound(Spark.state.currentTeam.id);
                outboundConnected.value = false;
                toasted.success('Amplitude outbound integration disconnected successfully.');
            } catch (error) {
                toasted.error(catchRequestError(error));
            } finally {
                disconnectingOutbound.value = false;
            }
        }
    );
};

const saveApiKey = async () => {
    const trimmedKey = apiKeyInput.value.trim();

    if ('' === trimmedKey) {
        toasted.error('API key is required.');
        return;
    }

    savingApiKey.value = true;
    try {
        await teamsClient.connectAmplitudeOutbound(Spark.state.currentTeam.id, trimmedKey);
        outboundConnected.value = true;
        apiKeyInput.value = '';
        toasted.success('Amplitude outbound integration connected successfully.');
    } catch (error) {
        toasted.error(catchRequestError(error));
    } finally {
        savingApiKey.value = false;
    }
};

const copyValue = (value: string | undefined, label: string) => {
    if (!value) {
        return;
    }
    navigator.clipboard
        .writeText(value)
        .then(() => {
            toasted.success(`${label} copied to clipboard.`);
        })
        .catch((error) => {
            console.error(error);
            toasted.error(`Failed to copy ${label}.`);
        });
};

const closeCredentialsModal = () => {
    showCredentialsModal.value = false;
    credentials.value = null;
};
</script>
